import Unavailable from 'components/_pages/booking/Form/Unavailable';
import { WideSelectOption } from 'components/_pages/booking/Form/WideSelect';
import dayjs from 'dayjs';
import i18n from 'providers/Locale';
import { BlockedTime, BookingDateException, BookingSettings, WorkTimeType } from 'shared/types';

import { getDateFromWorkTime } from '../../helpers';
import getDiffInMinutes from '../../helpers/getDiffInMinutes';
import getNearestDateByInterval from '../../helpers/getNearestDateByInterval';
import { getTimeOptions as getOrderTimeOptions } from '../orders/index';

type Props = {
	date: string;
	blockedTimes?: BlockedTime[];
	workTimeAll: WorkTimeType[];
	bookingSettings: BookingSettings;
	visitDuration?: number;
};

/**
 * Get booking available day times based on place workTime, booking exceptions and blocked times
 **/
const getTimeOptions = ({
	blockedTimes,
	workTimeAll,
	date,
	bookingSettings,
	visitDuration,
}: Props) => {
	const workTime = _getWorkTime({
		date,
		exceptions: bookingSettings.workingHours?.exceptions,
		bookingSettings,
		workTimeAll,
		visitDuration,
	});

	if (dayjs(workTime.fromDelayed).isAfter(workTime.tillDelayed)) return [];

	const options: WideSelectOption[] = getOrderTimeOptions({
		date: workTime.fromDelayed,
		workTimeAll,
		ownDateFromWorkTime: workTime,
		asapTimeDelayInMinutes: 5,
	});

	return _getFilteredTimeOptions({ options, blockedTimes, visitDuration });
};

/**
 * Get booking work time schedule for specific date based on exceptions and place working time
 * */
const _getWorkTime = ({
	workTimeAll,
	exceptions,
	date,
	bookingSettings,
	visitDuration,
}: {
	date: string;
	workTimeAll: WorkTimeType[];
	exceptions?: BookingDateException[];
	bookingSettings: BookingSettings;
	visitDuration?: number;
}) => {
	const dayDate = dayjs(date);
	const dayException = exceptions?.find((e) => e.date === date);
	const dayWorkTime = workTimeAll?.find((e) => e.dayOfWeek === dayDate.day());
	const workTime = {
		...dayWorkTime!,
		from: dayException?.from ?? dayWorkTime?.from!,
		till: dayException?.till ?? dayWorkTime?.till!,
		active: !!dayException,
	};

	const noDelayWorkTime = getDateFromWorkTime({
		workTime,
		date: dayDate.toDate(),
	});

	const withDelay = bookingSettings.minIntervalBefore - getDiffInMinutes(noDelayWorkTime.from);

	const dateWorkTime = getDateFromWorkTime({
		workTime,
		date: dayDate.toDate(),
		delay: withDelay > 0 ? withDelay : 0,
	});

	const isNotYetStarted = dayjs(noDelayWorkTime.from).isAfter();

	const fromDelayed =
		dayDate.isToday() && !isNotYetStarted
			? getNearestDateByInterval({
					date: dayjs()
						.add(bookingSettings.minIntervalBefore + 5, 'minutes')
						.toDate(),
			  })
			: dateWorkTime.fromDelayed;

	const minIntervalBeforeClose = _getMinIntervalBeforeClose({ bookingSettings, visitDuration });

	return {
		from: dateWorkTime.from,
		fromDelayed: fromDelayed,
		till: dateWorkTime.till,
		tillDelayed: dayjs(dateWorkTime.till).add(-minIntervalBeforeClose, 'minute').toDate(),
	};
};

/**
 * Get amount of minutes that we should extract from end of work time, that will be min interval before close
 * */
const _getMinIntervalBeforeClose = ({
	bookingSettings,
	visitDuration,
}: {
	bookingSettings: BookingSettings;
	visitDuration?: number;
}) => {
	return Math.max(bookingSettings.minIntervalBeforeClosed ?? 0, visitDuration ?? 0);
};

/**
 * Get filtered time options by block times and visit duration
 * */
const _getFilteredTimeOptions = ({
	options,
	blockedTimes,
	visitDuration,
}: {
	options: WideSelectOption[];
	blockedTimes?: BlockedTime[];
	visitDuration: number | undefined;
}) => {
	if (!blockedTimes?.length) return options;

	const blockedSchedules: string[] = [];
	const _filteredOptions: WideSelectOption[] = [];
	options.forEach((option) => {
		const blocks = blockedTimes.filter((block) => dayjs(block.date).isSame(option.value, 'date'));

		if (blocks.find((b) => b.allDay)) return;

		if (blocks.length) {
			const blockedOption = _getBlockedOption({ blockedSchedules, blocks, option, visitDuration });

			if (blockedOption) _filteredOptions.push(blockedOption);
		} else {
			_filteredOptions.push(option);
		}
	});

	return _filteredOptions;
};

/**
 * Get is options blocked and not yet filtered;
 * */
const _getBlockedOption = ({
	blockedSchedules,
	option,
	blocks,
	visitDuration,
}: {
	blockedSchedules: string[];
	option: WideSelectOption;
	blocks: BlockedTime[];
	visitDuration?: number | undefined;
}) => {
	const optionTime = dayjs(option.value);
	const optionTimeWithDuration = dayjs(option.value).add(visitDuration ?? 0, 'minutes');

	const blockTimes = blocks.map((block) => ({
		schedule: getDateFromWorkTime({
			workTime: block.interval,
			date: new Date(block.date),
		}),
		block,
	}));

	const optionBlockTime = blockTimes.find((block) => {
		const isSame = optionTime.isSame(block.schedule.from);
		const isBetween =
			optionTime.isAfter(block.schedule.from) && optionTime.isBefore(block.schedule.till);

		return isSame || isBetween;
	});

	let optionBlockTimeWithDuration = undefined;

	if (!optionBlockTime) {
		optionBlockTimeWithDuration = blockTimes.find(
			(block) =>
				optionTimeWithDuration.isAfter(block.schedule.from) &&
				optionTime.isBefore(block.schedule.from)
		);
	}

	if (optionBlockTimeWithDuration) {
		return null;
	} else if (optionBlockTime) {
		const blockSchedule = `${optionBlockTime.schedule.from.getTime()} - ${optionBlockTime.schedule.till.getTime()}`;
		if (!blockedSchedules.includes(blockSchedule)) {
			blockedSchedules.push(blockSchedule);
			return {
				...option,
				disabled: true,
				labelJSX: {
					left: `${optionTime.format('HH:mm')} - ${dayjs(optionBlockTime.schedule.till).format(
						'HH:mm'
					)}`,
					right: (
						<Unavailable
							title={i18n('option_unavailable')}
							description={optionBlockTime.block?.comment ?? null}
						/>
					),
				},
			};
		}
	} else {
		return option;
	}
};

export default getTimeOptions;
